import { DashboardNote } from "../checks/types";
import { Contact } from "../orderEntry/types/Contact";

export interface SurveyZoningOrderedDetails {
  contactEmail: string;
  contactLookupCode: string;
  contactName: string;
  eta: string | null;
}

type SurveyZoningStage =
  | "ordered"
  | "waiting_for_quotes"
  | "waiting_on_one_or_more_quotes"
  | "waiting_on_client_choice"
  | "followed_up_with_client"
  | "waiting_on_client_signature"
  | "signature_reminded"
  | "waiting_on_proposal"
  | "proposal_reminded"
  | "surveyor_contacted";
type SurveyZoningEtaProgress = "eta_accuracy_requested" | "eta_confirmed";
type SurveyZoningTrackedStatus = "Ordered" | "Received" | "Received, need invoice" | "N/A";

const ORDERED_DETAILS_PREFIX = "Survey/Zoning Ordered";
const STATUS_NOTE_PREFIX = "Survey/Zoning Status:";
const RECEIVED_DATE_PREFIX = "Survey/Zoning Received Date:";
const RECEIVED_DATE_CLEARED_VALUE = "Cleared";
const STATUS_REASON_NOTE_PREFIX = "Survey/Zoning Status Note:";
const WAITING_FOR_QUOTES_PREFIX = "Survey/Zoning Stage: Waiting for quotes";
const WAITING_ON_ONE_OR_MORE_QUOTES_PREFIX = "Survey/Zoning Stage: Waiting on 1+ quote";
const WAITING_ON_CLIENT_CHOICE_PREFIX = "Survey/Zoning Stage: Waiting on client choice";
const FOLLOWED_UP_WITH_CLIENT_PREFIX = "Survey/Zoning Stage: Followed up with client";
const NO_RESPONSE_FROM_CLIENT_PREFIX = "Survey/Zoning Stage: No response from client";
const WAITING_ON_CLIENT_SIGNATURE_PREFIX = "Survey/Zoning Stage: Waiting on client signature";
const SIGNATURE_REMINDED_PREFIX = "Survey/Zoning Stage: Signature reminded";
const WAITING_ON_PROPOSAL_PREFIX = "Survey/Zoning Stage: Waiting on Proposal";
const PROPOSAL_REMINDED_PREFIX = "Survey/Zoning Stage: Proposal reminded";
const SURVEYOR_CONTACTED_PREFIX = "Survey/Zoning Stage: Surveyor Contacted";
const ETA_ACCURACY_REQUESTED_NOTE = "ETA Accuracy Requested";
const ETA_CONFIRMED_NOTE = "ETA Confirmed";
const NOTE_EMPTY_VALUE = "-";
const NOTE_FIELD_SEPARATOR = " | ";
const SELECT_NOTE_PREFIX = "added this note: ";
const ORDERED_DETAILS_FIELDS = {
  contact: "Contact",
  email: "Email",
  eta: "ETA",
  lookupCode: "Lookup Code",
  requestedFrom: "Requested From"
} as const;
const COMPANY_FIELD = "Company";

const cleanValue = (value?: string | null) => value?.trim() || NOTE_EMPTY_VALUE;

const parseSlashDateToIso = (value: string) => {
  const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!match) {
    return null;
  }

  const [, monthText, dayText, yearText] = match;
  const month = Number(monthText);
  const day = Number(dayText);
  const year = yearText.length === 2 ? 2000 + Number(yearText) : Number(yearText);
  const parsed = new Date(year, month - 1, day);

  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const normalizeEtaForInput = (value?: string | null) => {
  const trimmed = value?.trim();
  if (!trimmed || trimmed === "-") {
    return null;
  }

  const slashDate = parseSlashDateToIso(trimmed);
  if (slashDate) {
    return slashDate;
  }

  const parsed = new Date(trimmed);
  return Number.isNaN(parsed.getTime()) ? trimmed : parsed.toISOString();
};

const getPlainNoteText = (value?: string | null) => {
  if (!value) return "";
  const splitValue = value.split(SELECT_NOTE_PREFIX);
  return splitValue.length > 1 ? splitValue[1] : splitValue[0];
};

const getSortedNotes = (notes?: DashboardNote[] | null) =>
  [...(Array.isArray(notes) ? notes : [])].sort(
    (a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
  );

const normalizeTaskDescription = (value?: string | null) => value?.trim().toLowerCase() || "";

const parseStructuredNote = (value: string | null | undefined, prefix: string) => {
  const plainText = getPlainNoteText(value);
  const prefixIndex = plainText.indexOf(prefix);
  if (prefixIndex < 0) {
    return null;
  }

  const relevantText = plainText.slice(prefixIndex);
  const parts = relevantText.split(NOTE_FIELD_SEPARATOR).map((part) => part.trim());
  if (!parts.length || parts[0] !== prefix) {
    return null;
  }

  return new Map(
    parts.slice(1).map((part) => {
      const separatorIndex = part.indexOf(":");
      if (separatorIndex < 0) {
        return [part, ""];
      }

      return [part.slice(0, separatorIndex).trim(), part.slice(separatorIndex + 1).trim()];
    })
  );
};

const getStructuredFieldValue = (entries: Map<string, string>, ...fieldNames: string[]) => {
  for (const fieldName of fieldNames) {
    const value = entries.get(fieldName)?.trim();
    if (value) {
      return value;
    }
  }

  return "";
};

const normalizeStructuredFieldValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return !trimmed || trimmed === NOTE_EMPTY_VALUE ? "" : trimmed;
};

const findLatestMatchingNote = <T>(notes: DashboardNote[] | null | undefined, parser: (note: DashboardNote) => T | null) => {
  for (const note of getSortedNotes(notes)) {
    const parsed = parser(note);
    if (parsed !== null) {
      return parsed;
    }
  }

  return null;
};

export const filterSurveyZoningNotesForTask = (
  notes: DashboardNote[] | null | undefined,
  taskDescription: string
) => {
  const normalizedTaskDescription = normalizeTaskDescription(taskDescription);
  const notesArray = Array.isArray(notes) ? notes : [];

  return notesArray.filter((note) => {
    const noteTaskDescription = normalizeTaskDescription(note.taskDescription);
    return !noteTaskDescription || noteTaskDescription === normalizedTaskDescription;
  });
};

export const getOrderedDetailsContactName = (contact: Contact | null) => {
  const fullName = [contact?.firstName, contact?.middleName, contact?.lastName].filter(Boolean).join(" ").trim();
  return contact?.company?.trim() || contact?.name?.trim() || fullName;
};

const buildStructuredNote = (prefix: string, fields: Array<[string, string | null | undefined]>) =>
  [prefix, ...fields.map(([label, value]) => `${label}: ${cleanValue(value)}`)].join(NOTE_FIELD_SEPARATOR);

export const buildOrderedDetailsNote = ({ contact }: { contact: Contact | null }) =>
  buildStructuredNote(ORDERED_DETAILS_PREFIX, [
    [ORDERED_DETAILS_FIELDS.requestedFrom, getOrderedDetailsContactName(contact)]
  ]);

export const parseOrderedDetailsNote = (value?: string | null): SurveyZoningOrderedDetails | null => {
  const entries = parseStructuredNote(value, ORDERED_DETAILS_PREFIX);
  if (!entries) {
    return null;
  }

  const etaValue = getStructuredFieldValue(entries, ORDERED_DETAILS_FIELDS.eta);
  const eta = normalizeEtaForInput(etaValue);
  const requestedFromValue = getStructuredFieldValue(
    entries,
    ORDERED_DETAILS_FIELDS.requestedFrom,
    ORDERED_DETAILS_FIELDS.contact
  );

  return {
    eta,
    contactName: normalizeStructuredFieldValue(requestedFromValue),
    contactEmail: normalizeStructuredFieldValue(getStructuredFieldValue(entries, ORDERED_DETAILS_FIELDS.email)),
    contactLookupCode: normalizeStructuredFieldValue(getStructuredFieldValue(entries, ORDERED_DETAILS_FIELDS.lookupCode))
  };
};

export const buildWaitingForQuotesNote = (company?: string | null) =>
  buildStageNote(WAITING_FOR_QUOTES_PREFIX, company);

export const isWaitingForQuotesNote = (value?: string | null) =>
  getPlainNoteText(value).includes(WAITING_FOR_QUOTES_PREFIX);

export const buildWaitingOnOneOrMoreQuotesNote = (company?: string | null) =>
  buildStageNote(WAITING_ON_ONE_OR_MORE_QUOTES_PREFIX, company);

export const isWaitingOnOneOrMoreQuotesNote = (value?: string | null) =>
  getPlainNoteText(value).includes(WAITING_ON_ONE_OR_MORE_QUOTES_PREFIX);

export const buildWaitingOnClientChoiceNote = () => WAITING_ON_CLIENT_CHOICE_PREFIX;

export const isWaitingOnClientChoiceNote = (value?: string | null) =>
  getPlainNoteText(value).includes(WAITING_ON_CLIENT_CHOICE_PREFIX);

export const buildFollowedUpWithClientNote = () => FOLLOWED_UP_WITH_CLIENT_PREFIX;

export const isFollowedUpWithClientNote = (value?: string | null) =>
  getPlainNoteText(value).includes(FOLLOWED_UP_WITH_CLIENT_PREFIX);

export const buildNoResponseFromClientNote = () => NO_RESPONSE_FROM_CLIENT_PREFIX;

export const isNoResponseFromClientNote = (value?: string | null) =>
  getPlainNoteText(value).includes(NO_RESPONSE_FROM_CLIENT_PREFIX);

const buildStageNote = (prefix: string, company?: string | null) => {
  const trimmedCompany = company?.trim();
  return trimmedCompany ? `${prefix}${NOTE_FIELD_SEPARATOR}${COMPANY_FIELD}: ${trimmedCompany}` : prefix;
};

const getCompanyFromStageNote = (value: string | null | undefined, prefix: string) => {
  const entries = parseStructuredNote(value, prefix);
  return entries ? normalizeStructuredFieldValue(getStructuredFieldValue(entries, COMPANY_FIELD)) : "";
};

export const buildWaitingOnClientSignatureNote = (company?: string | null) =>
  buildStageNote(WAITING_ON_CLIENT_SIGNATURE_PREFIX, company);

export const isWaitingOnClientSignatureNote = (value?: string | null) =>
  getPlainNoteText(value).includes(WAITING_ON_CLIENT_SIGNATURE_PREFIX);

export const buildSignatureRemindedNote = () => SIGNATURE_REMINDED_PREFIX;

export const isSignatureRemindedNote = (value?: string | null) =>
  getPlainNoteText(value).includes(SIGNATURE_REMINDED_PREFIX);

export const buildWaitingOnProposalNote = (company?: string | null) =>
  buildStageNote(WAITING_ON_PROPOSAL_PREFIX, company);

export const isWaitingOnProposalNote = (value?: string | null) =>
  getPlainNoteText(value).includes(WAITING_ON_PROPOSAL_PREFIX);

export const buildProposalRemindedNote = () => PROPOSAL_REMINDED_PREFIX;

export const isProposalRemindedNote = (value?: string | null) =>
  getPlainNoteText(value).includes(PROPOSAL_REMINDED_PREFIX);

export const buildSurveyorContactedNote = () => SURVEYOR_CONTACTED_PREFIX;

export const isSurveyorContactedNote = (value?: string | null) =>
  getPlainNoteText(value).includes(SURVEYOR_CONTACTED_PREFIX);

export const getLatestSurveyZoningCompany = (
  notes: DashboardNote[] | null | undefined,
  status: "Waiting for quotes" | "Waiting on 1+ quote" | "Waiting on client signature" | "Waiting on proposal"
) => {
  const prefix = {
    "Waiting for quotes": WAITING_FOR_QUOTES_PREFIX,
    "Waiting on 1+ quote": WAITING_ON_ONE_OR_MORE_QUOTES_PREFIX,
    "Waiting on client signature": WAITING_ON_CLIENT_SIGNATURE_PREFIX,
    "Waiting on proposal": WAITING_ON_PROPOSAL_PREFIX
  }[status];

  return findLatestMatchingNote(notes, (note) => getCompanyFromStageNote(note.text, prefix) || null);
};

export const getLatestWaitingForQuotesDate = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) => (isWaitingForQuotesNote(note.text) ? note.dateCreated : null));

export const getLatestQuoteWaitingDate = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) =>
    isWaitingForQuotesNote(note.text) || isWaitingOnOneOrMoreQuotesNote(note.text) ? note.dateCreated : null
  );

export const getLatestClientChoiceDate = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) =>
    isWaitingOnClientChoiceNote(note.text) ? note.dateCreated : null
  );

export const getLatestClientFollowUpDate = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) =>
    isFollowedUpWithClientNote(note.text) ? note.dateCreated : null
  );

export const getLatestClientSignatureDate = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) =>
    isWaitingOnClientSignatureNote(note.text) ? note.dateCreated : null
  );

export const getLatestSignatureReminderDate = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) =>
    isSignatureRemindedNote(note.text) ? note.dateCreated : null
  );

export const getLatestProposalDate = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) =>
    isWaitingOnProposalNote(note.text) ? note.dateCreated : null
  );

export const getLatestProposalReminderDate = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) =>
    isProposalRemindedNote(note.text) ? note.dateCreated : null
  );

export const getLatestSurveyorContactedDate = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) =>
    isSurveyorContactedNote(note.text) ? note.dateCreated : null
  );

export const buildSurveyZoningStatusNote = (status: SurveyZoningTrackedStatus) => `${STATUS_NOTE_PREFIX} ${status}`;

export const parseSurveyZoningStatusNote = (value?: string | null): SurveyZoningTrackedStatus | null => {
  const plainText = getPlainNoteText(value).trim();
  if (!plainText.startsWith(STATUS_NOTE_PREFIX)) {
    return null;
  }

  const status = plainText.slice(STATUS_NOTE_PREFIX.length).trim();
  if (status === "Received, Need Invoice" || status === "Received, need invoice") {
    return "Received, need invoice";
  }

  return status === "Ordered" || status === "Received" || status === "N/A" ? status : null;
};

export const isSurveyZoningStatusNote = (value?: string | null) => parseSurveyZoningStatusNote(value) !== null;

export const getLatestSurveyZoningStatusNoteDate = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) => (parseSurveyZoningStatusNote(note.text) ? note.dateCreated : null));

export const getLatestSurveyZoningStatus = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) => parseSurveyZoningStatusNote(note.text));

export const buildReceivedDateNote = (date: Date) =>
  `${RECEIVED_DATE_PREFIX} ${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

export const buildReceivedDateClearedNote = () => `${RECEIVED_DATE_PREFIX} ${RECEIVED_DATE_CLEARED_VALUE}`;

export const getLatestReceivedDate = (notes?: DashboardNote[] | null) => {
  for (const note of getSortedNotes(notes)) {
    const plainText = getPlainNoteText(note.text).trim();
    if (!plainText.startsWith(RECEIVED_DATE_PREFIX)) continue;

    const value = plainText.slice(RECEIVED_DATE_PREFIX.length).trim();
    return value === RECEIVED_DATE_CLEARED_VALUE ? null : value;
  }

  return null;
};

export const buildSurveyZoningReasonNote = (text: string) => `${STATUS_REASON_NOTE_PREFIX} ${text.trim()}`;

export const getLatestSurveyZoningStatusNote = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) => {
    const plainText = getPlainNoteText(note.text).trim();
    if (!plainText.startsWith(STATUS_REASON_NOTE_PREFIX)) return null;

    const value = plainText.slice(STATUS_REASON_NOTE_PREFIX.length).trim();
    return value || null;
  });

export const buildEtaAccuracyRequestedNote = () => ETA_ACCURACY_REQUESTED_NOTE;

export const buildEtaConfirmedNote = () => ETA_CONFIRMED_NOTE;

export const parseEtaProgressNote = (value?: string | null): SurveyZoningEtaProgress | null => {
  const plainText = getPlainNoteText(value).trim();

  if (plainText === ETA_CONFIRMED_NOTE) {
    return "eta_confirmed";
  }

  if (plainText === ETA_ACCURACY_REQUESTED_NOTE) {
    return "eta_accuracy_requested";
  }

  return null;
};

export const getLatestSurveyZoningEtaProgress = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) => parseEtaProgressNote(note.text));

export const getLatestSurveyZoningEtaProgressDate = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) => (parseEtaProgressNote(note.text) ? note.dateCreated : null));

const getStageFromNote = (note: DashboardNote): SurveyZoningStage | null => {
  if (parseOrderedDetailsNote(note.text)) {
    return "ordered";
  }

  if (isWaitingForQuotesNote(note.text)) {
    return "waiting_for_quotes";
  }

  if (isWaitingOnOneOrMoreQuotesNote(note.text)) {
    return "waiting_on_one_or_more_quotes";
  }

  if (isWaitingOnClientChoiceNote(note.text)) {
    return "waiting_on_client_choice";
  }

  if (isFollowedUpWithClientNote(note.text)) {
    return "followed_up_with_client";
  }

  if (isWaitingOnClientSignatureNote(note.text)) {
    return "waiting_on_client_signature";
  }

  if (isSignatureRemindedNote(note.text)) {
    return "signature_reminded";
  }

  if (isWaitingOnProposalNote(note.text)) {
    return "waiting_on_proposal";
  }

  if (isProposalRemindedNote(note.text)) {
    return "proposal_reminded";
  }

  if (isSurveyorContactedNote(note.text)) {
    return "surveyor_contacted";
  }

  return null;
};

export const getLatestSurveyZoningStage = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, getStageFromNote);

export const getLatestOrderedDetails = (notes?: DashboardNote[] | null) =>
  findLatestMatchingNote(notes, (note) => parseOrderedDetailsNote(note.text));
